import React, { useState } from 'react';
import { useHistory, Redirect } from 'react-router';
import { formatPhone } from '../helpers/phoneUtils';

import {
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    CircularProgress,
    Grid,
    TextField,
    IconButton,
    Chip,
    Checkbox,
    FormControlLabel,
    Tabs,
    Tab
} from '@material-ui/core';
import TablePager from './atom/TablePager';
import { useSwaggerClient } from '../hooks/useSwaggerClient';
import SortableTableCell from './atom/SortableTableCell';
import { useSorting } from '../hooks/useSorting';
import { useModals } from '../hooks/useModal';
import { TimesIcon, LoginIcon, LogoutIcon, UserIcon } from '../icons';
import ActionIconButton from './atom/ActionIconButton';
import { resetUrlFilters } from '../pages/activitycards/components/urlFiltering';

export default function GuestTable({
    user,
    customer,
    closeGuestTable,
    loginAsCustomerWithId,
    cartItemCount
}) {
    const history = useHistory();
    const { showModal } = useModals();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');
    const [crossSiteSearch, setCrossSiteSearch] = useState(false);
    const [selectedClientTab, setSelectedClientTab] = useState(null);
    const { sort, sortDir, setSorting } = useSorting(['email', 'ASC']);
    const [guests, loading] = useSwaggerClient('users', 'getUsers', {
        page,
        pageSize,
        search,
        sort,
        sortDir,
        crossSiteSearch,
        targetClient: selectedClientTab
    });

    if (user === null) {
        return <Redirect to="/" />;
    }

    // admins are not limited to the current client, they get every client's guests
    // in one list with the owning client shown on each row
    const isAdmin = user?.role === 'admin';

    // An admin sees every client's guests, but impersonation only works inside the
    // client they are logged into: createBooking resolves the booker within the
    // current company, so a cart filled as a foreign-client guest would 404 at
    // checkout. Their detail page still opens, the way booking details do.
    const canLoginAs = guest => {
        if (!isAdmin || !guests?.currentClientInfo?.id) {
            return true;
        }
        const owner = guest.company?.id ?? guest.company_id;
        return !owner || owner === guests.currentClientInfo.id;
    };

    if (!loading && !guests) {
        return null;
    }

    // how many different user groups the guest has
    const userGroupsUnique = [];
    if (Array.isArray(guests?.guests)) {
        guests?.guests?.forEach(g => {
            if (g.user_groups.length === 0 && userGroupsUnique.includes(null) === false) {
                userGroupsUnique.push(null);
            }
            g.user_groups?.forEach(ug => {
                if (!userGroupsUnique.includes(ug.id)) {
                    userGroupsUnique.push(ug.id);
                }
            });
        });
    }
    return (
        <Grid container className="bg-white">
            <Grid container item xs={12} alignItems="center" spacing={2} className="mb-2">
                <Grid item className="pl-3">
                    Enter name, email or phone number to search:{' '}
                </Grid>
                <Grid item>
                    <form
                        onSubmit={ev => {
                            // when we have only one result, form submit will fo the login
                            if (guests && guests.guests.length === 1) {
                                closeGuestTable();
                                const customerId = guests.guests[0].id;
                                loginAsCustomerWithId(customerId, showModal, () => {
                                    // navigate on success
                                    history.push(`/guests/${customerId}`);
                                });
                            }
                            ev.preventDefault();
                        }}
                    >
                        <TextField
                            value={search}
                            onChange={ev => {
                                setSearch(ev.target.value);
                                setPage(0);
                            }}
                        />
                    </form>
                </Grid>
                {!isAdmin && (
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={crossSiteSearch}
                                    onChange={ev => {
                                        setCrossSiteSearch(ev.target.checked);
                                        setSelectedClientTab(null);
                                        setPage(0);
                                    }}
                                    color="primary"
                                />
                            }
                            label="Cross site search"
                        />
                    </Grid>
                )}
                <Grid item>
                    {guests &&
                        guests.guests.length === 1 &&
                        !(customer && customer.id === guests.guests[0].id) && (
                            <em>
                                Press Enter to login as {guests.guests[0].firstname}{' '}
                                {guests.guests[0].lastname}
                            </em>
                        )}
                </Grid>
                <Grid item className="ml-auto">
                    <IconButton onClick={closeGuestTable}>
                        <TimesIcon />
                    </IconButton>
                </Grid>
            </Grid>
            {crossSiteSearch && guests?.currentClientInfo && (
                <Grid item xs={12}>
                    <Tabs
                        value={selectedClientTab || false}
                        onChange={(ev, newValue) => {
                            setSelectedClientTab(newValue);
                            setPage(0);
                        }}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab
                            label={`${guests.currentClientInfo.name} (${guests.currentClientInfo.total})`}
                            value={null}
                        />
                        {guests.otherClients?.map(client => (
                            <Tab
                                key={client.id}
                                label={`${client.name} (${client.total})`}
                                value={client.id}
                            />
                        ))}
                    </Tabs>
                </Grid>
            )}
            <Grid item xs={12}>
                {loading && guests?.guests.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <SortableTableCell
                                    arrow={sort === 'firstname' ? sortDir : ''}
                                    onClick={() => setSorting('firstname')}
                                >
                                    First Name
                                </SortableTableCell>
                                <SortableTableCell
                                    arrow={sort === 'lastname' ? sortDir : ''}
                                    onClick={() => setSorting('lastname')}
                                >
                                    Last Name
                                </SortableTableCell>
                                <SortableTableCell
                                    arrow={sort === 'email' ? sortDir : ''}
                                    onClick={() => setSorting('email')}
                                >
                                    Email
                                </SortableTableCell>
                                <SortableTableCell
                                    arrow={sort === 'phone' ? sortDir : ''}
                                    onClick={() => setSorting('phone')}
                                >
                                    Phone
                                </SortableTableCell>
                                {isAdmin && (
                                    <SortableTableCell
                                        arrow={sort === 'company' ? sortDir : ''}
                                        onClick={() => setSorting('company')}
                                    >
                                        Client
                                    </SortableTableCell>
                                )}
                                {userGroupsUnique.length > 1 && <TableCell>User group</TableCell>}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {guests?.guests.map(guest => (
                                <TableRow key={guest.id}>
                                    <TableCell>{guest.firstname}</TableCell>
                                    <TableCell>{guest.lastname}</TableCell>
                                    <TableCell>{guest.email}</TableCell>
                                    <TableCell>{formatPhone(guest.phone)}</TableCell>
                                    {isAdmin && <TableCell>{guest.company?.name}</TableCell>}
                                    {userGroupsUnique.length > 1 && (
                                        <TableCell>
                                            {guest.user_groups?.map((grp, idx) => {
                                                return <Chip key={idx} label={grp.name} />;
                                            })}
                                        </TableCell>
                                    )}
                                    <TableCell style={{ width: 100 }}>
                                        {customer && customer.id === guest.id && (
                                            <ActionIconButton
                                                title="Logout"
                                                icon={LogoutIcon}
                                                onClick={() => {
                                                    loginAsCustomerWithId(null, showModal, () => {
                                                        closeGuestTable();
                                                    });
                                                    if (cartItemCount === 0) {
                                                        history.replace(resetUrlFilters());
                                                    }
                                                }}
                                            />
                                        )}
                                        {(!customer || customer.id !== guest.id) &&
                                            canLoginAs(guest) && (
                                                <ActionIconButton
                                                    title="Login as guest"
                                                    icon={LoginIcon}
                                                    onClick={() => {
                                                        const customerId = guest.id;
                                                        // For cross-site guests, open in new tab with urlPrefix
                                                        if (guests.urlPrefix && selectedClientTab) {
                                                            window.open(
                                                                `${guests.urlPrefix}&action=login&guestId=${customerId}`,
                                                                '_blank'
                                                            );
                                                        } else {
                                                            loginAsCustomerWithId(
                                                                customerId,
                                                                showModal,
                                                                () => {
                                                                    closeGuestTable();
                                                                }
                                                            );
                                                        }
                                                    }}
                                                />
                                            )}
                                        {guests.urlPrefix && selectedClientTab ? (
                                            <ActionIconButton
                                                title="View guest"
                                                icon={UserIcon}
                                                onClick={() => {
                                                    window.open(
                                                        `${guests.urlPrefix}&action=view&guestId=${guest.id}`,
                                                        '_blank'
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <ActionIconButton
                                                title="View guest"
                                                icon={UserIcon}
                                                to={`/guests/${guest.id}`}
                                                onClick={closeGuestTable}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={guests?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}
